// Vendored, self-contained calendar-grid math for the demo month view — a copy
// of the real app's lib/calendar/grid.ts + the DST helpers it depends on, inlined
// so the isolation-guarded demo imports NO real-app code (Intl/Date only, no
// `@/lib/...`, no Node built-ins). Every "calendar day" is identified by its
// LOCAL noon in `timezone`, which round-trips through localDate for every zone.

const DAY_MS = 24 * 60 * 60 * 1000;

function offsetMinutes(date: Date, timezone: string): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts: Record<string, string> = {};
  for (const p of dtf.formatToParts(date)) if (p.type !== "literal") parts[p.type] = p.value;
  const hour = parts.hour === "24" ? 0 : Number(parts.hour);
  const asUtc = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), hour, Number(parts.minute), Number(parts.second));
  return (asUtc - date.getTime()) / 60000;
}

export function localDate(date: Date, timezone: string): { year: number; month: number; day: number; weekday: number } {
  const dtf = new Intl.DateTimeFormat("en-US", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" });
  const parts: Record<string, string> = {};
  for (const p of dtf.formatToParts(date)) if (p.type !== "literal") parts[p.type] = p.value;
  const year = Number(parts.year);
  const month = Number(parts.month);
  const day = Number(parts.day);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return { year, month, day, weekday };
}

export function zonedWallToUtc(year: number, month: number, day: number, hour: number, minute: number, timezone: string): Date {
  const guess = Date.UTC(year, month - 1, day, hour, minute);
  const off1 = offsetMinutes(new Date(guess), timezone);
  let utc = guess - off1 * 60000;
  const off2 = offsetMinutes(new Date(utc), timezone);
  if (off2 !== off1) utc = guess - off2 * 60000;
  return new Date(utc);
}

function dayId(year: number, month: number, day: number, timezone: string): Date {
  return zonedWallToUtc(year, month, day, 12, 0, timezone);
}
function addDays(d: Date, n: number): Date {
  return new Date(d.getTime() + n * DAY_MS);
}

export function dayKey(date: Date, timezone: string): string {
  const { year, month, day } = localDate(date, timezone);
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export interface MonthCell {
  date: Date;
  inMonth: boolean;
}

export function monthGrid(anchor: Date, timezone: string): MonthCell[] {
  const ld = localDate(anchor, timezone);
  const first = dayId(ld.year, ld.month, 1, timezone);
  const firstDow = localDate(first, timezone).weekday;
  const gridStart = addDays(first, -firstDow);
  const cells: MonthCell[] = [];
  for (let i = 0; i < 42; i++) {
    const date = addDays(gridStart, i);
    cells.push({ date, inMonth: localDate(date, timezone).month === ld.month });
  }
  return cells;
}

/** Shift the anchor by one local calendar month (dir -1 prev, +1 next, 0 today). */
export function navigateMonth(anchor: Date, dir: -1 | 0 | 1, timezone: string): Date {
  if (dir === 0) return new Date();
  const ld = localDate(anchor, timezone);
  const target0 = ld.month - 1 + dir;
  const ty = ld.year + Math.floor(target0 / 12);
  const tm0 = ((target0 % 12) + 12) % 12;
  const daysInTarget = new Date(Date.UTC(ty, tm0 + 1, 0)).getUTCDate();
  return dayId(ty, tm0 + 1, Math.min(ld.day, daysInTarget), timezone);
}

export function monthLabel(anchor: Date, timezone: string): string {
  return new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric", timeZone: timezone }).format(anchor);
}
